import { useEffect, useState, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Box, Button, Grid, Typography, Alert
} from '@mui/material';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import DropZone from '../common/DropZone';
import { generateSlug } from '../../utils/slugGenerator';
import { uploadSingleImage } from '../../services/upload.service';

// Slug removed from schema — auto-generated
const necropolisSchema = z.object({
  slug: z.string().optional(), // Hidden, auto-generated
  name: z.string().min(1, 'Укажите имя'),
  rank: z.string().optional(),
  years: z.string().optional(),
  portraitUrl: z.string().optional(), // Uploaded via DropZone
  biography: z.string().optional(),
});

type NecropolisFormValues = z.infer<typeof necropolisSchema>;

interface NecropolisFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: NecropolisFormValues) => Promise<void>;
  initialData?: NecropolisFormValues | null;
  loading: boolean;
  error?: string | null;
}

export function NecropolisForm({ open, onClose, onSubmit, initialData, loading, error }: NecropolisFormProps) {
  const [portraitUploading, setPortraitUploading] = useState(false);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<NecropolisFormValues>({
    resolver: zodResolver(necropolisSchema),
    defaultValues: {
      slug: '',
      name: '',
      rank: '',
      years: '',
      portraitUrl: '',
      biography: '',
    },
  });

  const nameValue = watch('name');
  const portraitUrl = watch('portraitUrl');

  // Auto-generate slug when name changes (only for new items)
  useEffect(() => {
    if (!initialData && nameValue && nameValue.trim().length > 0) {
      const autoSlug = generateSlug(nameValue);
      setValue('slug', autoSlug);
    }
  }, [nameValue, initialData, setValue]);

  useEffect(() => {
    if (initialData) {
      reset(initialData);
    } else {
      reset({
        slug: '',
        name: '',
        rank: '',
        years: '',
        portraitUrl: '',
        biography: '',
      });
    }
  }, [initialData, open, reset]);

  // Handle image upload via DropZone
  const handleImageUpload = useCallback(async (files: File[]): Promise<{ url: string }[]> => {
    setPortraitUploading(true);
    try {
      const results: { url: string }[] = [];
      for (const file of files) {
        const url = await uploadSingleImage(file);
        results.push({ url });
      }
      return results;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Ошибка загрузки';
      throw new Error(message);
    } finally {
      setPortraitUploading(false);
    }
  }, []);

  const handlePortraitChange = useCallback((urls: string | string[]) => {
    const url = Array.isArray(urls) ? urls[0] : urls;
    setValue('portraitUrl', url);
  }, [setValue]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {initialData ? 'Редактировать запись некрополя' : 'Добавить запись некрополя'}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            {/* Name — main field */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="ФИО *"
                error={!!errors.name}
                helperText={errors.name?.message}
                {...register('name')}
              />
            </Grid>
            
            {/* Slug — hidden field, shown as read-only hint */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="URL (автоматически)"
                value={watch('slug') || ''}
                disabled
                helperText="Генерируется из имени"
                sx={{
                  '& .MuiInputBase-input.Mui-disabled': {
                    bgcolor: 'action.hover',
                  }
                }}
              />
            </Grid>
            
            {/* Rank */}
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Сан"
                {...register('rank')}
              />
            </Grid>
            
            {/* Years */}
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Годы жизни"
                placeholder="например: 1850-1920"
                {...register('years')}
              />
            </Grid>
            
            {/* Portrait — DropZone instead of URL TextField */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Портрет
              </Typography>
              <DropZone
                onUpload={handleImageUpload}
                type="image"
                maxFiles={1}
                maxSize={10}
                multiple={false}
                value={portraitUrl || ''}
                onChange={handlePortraitChange}
                label="Перетащите изображение или нажмите для выбора"
                helperText="JPG, PNG, WebP до 10MB"
              />
              {portraitUploading && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  Загрузка...
                </Typography>
              )}
            </Grid>
            
            {/* Biography */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Биография"
                multiline
                rows={4}
                {...register('biography')}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                * Обязательные поля
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Отмена</Button>
        <Button 
          variant="contained" 
          onClick={handleSubmit(onSubmit)} 
          disabled={loading || portraitUploading}
        >
          {loading ? 'Сохранение...' : 'Сохранить'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}